import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationExtras,
  QueryParamsHandling,
} from '@angular/router';
import { ActivityInterface } from '@sport-activities/sdk';

import { AutocompleteSuggestion } from '~/component/form/autocomplete/interfaces/suggestion.interface';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { isQueryParamTruthy } from '~/core/helpers/query-params/truthy/truthy.helper';
import { diacriticSort } from '~/core/helpers/sort/diacritic-sort.helper';
import { parseStringifyUrl } from '~/core/helpers/url/parse/parse.helper';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { TitleService } from '~/core/services/title/title.service';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { organizerSharedConfig } from '~/organizer/shared/configs/config';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

@Component({
  selector: 'app-organizer-activity-creation-sport',
  templateUrl: './sport.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageSportComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.sport.title';
  private activitySports: number[] = [];
  public autocompleteSuggestions: AutocompleteSuggestion[] = [];
  public displayValidationError = false;
  public previousNavigationExtras: NavigationExtras = {};
  public previousQueryParametersHandling: QueryParamsHandling = 'preserve';
  public previousUrl = '';

  constructor(
    private readonly sportsService: SportsService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly formControlService: FormControlService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get activitySportName(): string {
    if (!this.activityService.activity?.sports?.length) {
      return '';
    }

    return (
      this.sportsService.sports.find(
        (sport) => sport.id === this.activityService.activity?.sports?.[0]
      )?.name || ''
    );
  }

  private buildAutocompleteSuggestions(): void {
    this.autocompleteSuggestions = this.sportsService.sports
      .map(
        (sport): AutocompleteSuggestion => ({
          selected: this.activitySports.includes(sport.id),
          text: sport.name,
          value: sport.id,
        })
      )
      .sort(({ text: currentValue }, { text: nextValue }) =>
        diacriticSort(currentValue, nextValue)
      );
  }

  private buildUrlAndQueryParams(): void {
    const allQueryParams = this.activatedRoute.snapshot.queryParams;
    const { activityEdition, from } = organizerSharedConfig.queryParams;

    if (!isQueryParamTruthy(allQueryParams[activityEdition])) {
      return;
    }

    const { baseUrl, queryParams } = parseStringifyUrl(allQueryParams[from]);

    this.previousUrl = baseUrl;
    this.previousNavigationExtras = { queryParams };
    this.previousQueryParametersHandling = '';
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      sports: [+formGroupValue.sport],
    };
  }

  protected buildValidationConfig(): void {
    this.activitySports = this.activityService.activity?.sports || [];

    this.validationConfig = {
      sport: [
        this.activitySports.length ? this.activitySports?.[0] : '',
        Validators.required,
      ],
    };
  }

  protected onInitHook(): void {
    this.buildAutocompleteSuggestions();
    this.buildUrlAndQueryParams();
  }

  public errorMessages(inputName: string): string[] {
    const control = this.formGroup.get(inputName);
    return (
      this.displayValidationError &&
      this.formControlService.getErrorMessages(control, inputName)
    );
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.displayValidationError = true;
      return;
    }

    super.submit(formGroupValue);
  }
}
